import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, RefreshCw, Trash2, Shield, BarChart3, Target, Puzzle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';

interface CookieConsent {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

interface ConsentMetadata {
  consentDate: string;
  expiresAt: string;
  countryCode: string;
  isEuUser: boolean;
}

interface CookieCategoryFromDB {
  id: string;
  key: string;
  name_i18n: Record<string, string>;
  description_i18n: Record<string, string>;
  is_required: boolean;
  is_active: boolean;
  sort_order: number;
}

interface CookiePreferencesProps {
  isOpen: boolean;
  onClose: () => void;
}

// Détection du mode développement (localhost)
const isDev = typeof window !== 'undefined' && window.location.hostname === 'localhost';

const CookiePreferences: React.FC<CookiePreferencesProps> = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation('common');
  const { user } = useAuth(); // Hook pour récupérer l'utilisateur connecté
  const [consent, setConsent] = useState<CookieConsent>({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false,
  });
  
  const [metadata, setMetadata] = useState<ConsentMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [dbCategories, setDbCategories] = useState<CookieCategoryFromDB[]>([]);

  // Fetch cookie categories from Supabase
  const fetchCookieCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('cookie_categories')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) {
        return;
      }

      if (data && data.length > 0) {
        setDbCategories(data);
      }
    } catch {
      // Silent error handling - categories will use fallback
    }
  };

  // Fonction pour vérifier si l'utilisateur est exclu
  const isUserExcluded = (): boolean => {
    // Vérification si on est dans l'admin (URL contient /admin)
    if (window.location.pathname.includes('/admin')) {
      return true;
    }

    // Vérification si l'utilisateur est connecté et a un email @komby.io
    if (user?.email && user.email.endsWith('@komby.io')) {
      return true;
    }

    return false;
  };

  // Récupération des préférences actuelles
  const loadCurrentPreferences = async () => {
    setIsLoading(true);
    try {
      // Fetch cookie categories from Supabase
      await fetchCookieCategories();

      const sessionId = localStorage.getItem('komby_session_id') || 
                       `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      if (!localStorage.getItem('komby_session_id')) {
        localStorage.setItem('komby_session_id', sessionId);
      }

      // Try to load from Supabase first (check if user has existing consent)
      try {
        const { data: existingConsent } = await supabase
          .from('cookie_consents')
          .select('*')
          .eq('session_id', sessionId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (existingConsent) {
          setConsent({
            necessary: true,
            analytics: existingConsent.analytics_cookies || false,
            marketing: existingConsent.marketing_cookies || false,
            functional: existingConsent.functional_cookies || false,
          });
          setMetadata({
            consentDate: existingConsent.consent_date || existingConsent.created_at,
            expiresAt: existingConsent.expires_at || '',
            countryCode: existingConsent.country_code || 'Unknown',
            isEuUser: existingConsent.is_eu_user || false,
          });
          return;
        }
      } catch {
        // Fallback to localStorage
      }

      // Fallback : utiliser localStorage avec protection JSON
      const stored = localStorage.getItem('komby_cookie_consent');
      if (stored && stored.trim() !== '' && stored !== 'undefined' && stored !== 'null') {
        try {
          const localData = JSON.parse(stored);
          if (localData && localData.consent) {
            setConsent(localData.consent);
          }
        } catch {
          localStorage.removeItem('komby_cookie_consent');
        }
      }
    } catch {
      setMessage({ type: 'error', text: t('cookies.preferences.loadError', 'Error loading preferences') });
    } finally {
      setIsLoading(false);
    }
  };

  // Sauvegarde des préférences
  const savePreferences = async () => {
    setIsSaving(true);
    try {
      let sessionId = localStorage.getItem('komby_session_id');
      if (!sessionId) {
        sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('komby_session_id', sessionId);
      }

      // EU countries list for RGPD compliance
      const EU_COUNTRIES = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 
                           'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 
                           'SI', 'ES', 'SE', 'IS', 'LI', 'NO', 'CH'];

      // Détection géographique pour la conformité RGPD (tolérante aux CORS/timeout)
      let geoData: { country_code: string } = { country_code: 'FR' };
      try {
        const params = new URLSearchParams(location.search);
        const isDebug = params.has('ga_debug') || localStorage.getItem('ga_debug') === '1';
        if (!isDebug) {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 2500);
          const geoResponse = await fetch('https://ipapi.co/json/', { signal: controller.signal, mode: 'cors' as RequestMode });
          clearTimeout(timeout);
          if (geoResponse.ok) {
            geoData = await geoResponse.json();
          }
        }
      } catch {
        // Geo API failed, using fallback FR (EU)
      }
      
      // Save to Supabase via Edge Function (works in both dev and prod)
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const response = await fetch(`${supabaseUrl}/functions/v1/save-cookie-consent`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            session_id: sessionId,
            user_id: user?.id || null,
            necessary_cookies: true,
            analytics_cookies: !!consent.analytics,
            marketing_cookies: !!consent.marketing,
            functional_cookies: !!consent.functional,
            country_code: geoData.country_code,
            is_eu_user: EU_COUNTRIES.includes(geoData.country_code)
          })
        });

        // Silent - localStorage will be used as cache regardless
      } catch {
        // Edge Function call failed, using localStorage as fallback
      }

      // Always save to localStorage as cache
      const consentData = {
        consent,
        timestamp: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
        country: geoData.country_code,
        isEU: EU_COUNTRIES.includes(geoData.country_code),
        version: '1.0'
      };
      
      localStorage.setItem('komby_cookie_consent', JSON.stringify(consentData));
      
      setMessage({ 
        type: 'success', 
        text: t('cookies.preferences.saveSuccess', 'Preferences saved successfully') 
      });
      
      // Recharger la page pour appliquer les nouveaux consentements
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch {
      setMessage({ 
        type: 'error', 
        text: t('cookies.preferences.saveError', 'Error saving preferences') 
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Suppression complète des consentements
  const revokeAllConsents = async () => {
    if (!window.confirm(t('cookies.preferences.revokeConfirm', 'Are you sure you want to revoke all your consents?'))) {
      return;
    }

    setIsSaving(true);
    try {
      const sessionId = localStorage.getItem('komby_session_id');
      
      // API uniquement en mode développement
      if (isDev && sessionId) {
        try {
          await fetch(`/api/cookie-consent?session_id=${sessionId}`, {
            method: 'DELETE'
          });
        } catch {
          // API cookie-consent DELETE non disponible
        }
      }
      
      // Nettoyage local
      localStorage.removeItem('komby_cookie_consent');
      localStorage.removeItem('komby_session_id');
      
      // Suppression de tous les cookies non-essentiels
      document.cookie.split(";").forEach(cookie => {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        
        // Ne pas supprimer les cookies essentiels
        if (!['session_token', 'csrf_token', 'auth_token'].includes(name)) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        }
      });
      
      setMessage({ 
        type: 'success', 
        text: t('cookies.preferences.revokeSuccess', 'All consents have been revoked') 
      });
      
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      
    } catch {
      setMessage({ 
        type: 'error', 
        text: t('cookies.preferences.revokeError', 'Error revoking consents') 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const toggleCategory = (category: keyof Omit<CookieConsent, 'necessary'>) => {
    setConsent(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  useEffect(() => {
    if (isOpen && !isUserExcluded()) {
      loadCurrentPreferences();
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // ✅ Scroll lock: empêcher le défilement du body quand le modal est ouvert
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup au démontage du composant
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // ✅ Fermer le modal avec la touche Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Ne pas afficher le modal si l'utilisateur est exclu
  if (!isOpen || isUserExcluded()) return null;

  // Icon mapping for categories
  const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
    analytics: BarChart3,
    marketing: Target,
    functional: Puzzle,
  };

  // Hardcoded fallback categories (used if database fetch fails)
  const fallbackCategories = [
    {
      key: 'analytics' as const,
      icon: BarChart3,
      title: t('cookies.analytics.title', 'Analytics cookies'),
      description: t('cookies.analytics.description', 'Google Analytics, mesure d\'audience, statistiques de visite'),
      examples: t('cookies.analytics.examples', 'Examples: _ga, _gid, _gat'),
      duration: t('cookies.analytics.duration', 'Duration: 2 years'),
      required: false
    },
    {
      key: 'marketing' as const,
      icon: Target,
      title: t('cookies.marketing.title', 'Marketing cookies'),
      description: t('cookies.marketing.description', 'Targeted ads, social networks, remarketing'),
      examples: t('cookies.marketing.examples', 'Examples: _fbp, _ttp, _tt_enable_cookie, ads_id'),
      duration: t('cookies.marketing.duration', 'Duration: 3 months'),
      required: false
    },
    {
      key: 'functional' as const,
      icon: Puzzle,
      title: t('cookies.functional.title', 'Functional cookies'),
      description: t('cookies.functional.description', 'User preferences, language, theme'),
      examples: t('cookies.functional.examples', 'Examples: theme, language, currency'),
      duration: t('cookies.functional.duration', 'Duration: 1 year'),
      required: false
    }
  ];

  // Use database categories if available, otherwise use fallback
  const cookieCategories = dbCategories.length > 0 
    ? dbCategories
        .filter(cat => !cat.is_required) // Exclude 'necessary' category (shown separately)
        .map(cat => ({
          key: cat.key as 'analytics' | 'marketing' | 'functional',
          icon: iconMap[cat.key] || Puzzle,
          title: cat.name_i18n[i18n.language] || cat.name_i18n['en'] || cat.key,
          description: cat.description_i18n[i18n.language] || cat.description_i18n['en'] || '',
          examples: t(`cookies.${cat.key}.examples`, ''),
          duration: t(`cookies.${cat.key}.duration`, ''),
          required: cat.is_required
        }))
    : fallbackCategories;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
        <div className="pointer-events-none absolute inset-0" />
        
                  <div className="relative bg-white/90 dark:bg-gray-900/90 theme-black:bg-white/40 theme-black:dark:bg-gray-900/40 backdrop-blur-md rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
                      <div className="sticky top-0 bg-white/90 dark:bg-gray-900/90 theme-black:bg-white/40 theme-black:dark:bg-gray-900/40 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="h-6 w-6 text-yellow-500" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {t('cookies.preferences.title', 'Gestion des cookies')}
                </h2>
              </div>
              
              <button
                onClick={onClose}
                className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                ×
              </button>
            </div>
            
            {message && (
              <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
                message.type === 'success' 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              }`}>
                <AlertCircle className="h-4 w-4" />
                {message.text}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-yellow-500" />
                <span className="ml-3 text-gray-600 dark:text-gray-400">
                  {t('cookies.preferences.loading', 'Loading preferences...')}
                </span>
              </div>
            ) : (
              <>
                {/* Message spécial pour les utilisateurs exclus */}
                <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <h3 className="font-medium text-blue-900 dark:text-blue-200 mb-2">
                    {t('cookies.preferences.exemptUser', 'Exempt user')}
                  </h3>
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    {t('cookies.preferences.exemptMessage', 'En tant qu\'employé Komby ou administrateur, vous êtes exempt des exigences de consentement aux cookies RGPD.')}
                  </p>
                </div>

                {/* Informations sur le consentement actuel */}
                {metadata && (
                  <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <h3 className="font-medium text-blue-900 dark:text-blue-200 mb-2">
                      {t('cookies.preferences.currentConsent', 'Current consent')}
                    </h3>
                    <div className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
                      <p>
                        {t('cookies.preferences.consentDate', 'Consent date')}: {' '}
                        {new Date(metadata.consentDate).toLocaleDateString(i18n.language || 'en')}
                      </p>
                      <p>
                        {t('cookies.preferences.expires', 'Expires on')}: {' '}
                        {new Date(metadata.expiresAt).toLocaleDateString(i18n.language || 'en')}
                      </p>
                      <p>
                        {t('cookies.preferences.location', 'Location')}: {metadata.countryCode} {' '}
                        {metadata.isEuUser && '(UE)'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Necessary cookies */}
                <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Shield className="h-5 w-5 text-green-600" />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {t('cookies.necessary.title', 'Necessary cookies')}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {t('cookies.necessary.description', 'Essential for the site to work (security, authentication, session)')}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          {t('cookies.necessary.examples', 'Exemples : session_token, csrf_token, auth_token')}
                        </p>
                      </div>
                    </div>
                    <div className="text-green-600 font-medium text-sm">
                      {t('cookies.alwaysActive', 'Toujours actif')}
                    </div>
                  </div>
                </div>

                {/* Catégories optionnelles */}
                <div className="space-y-4 mb-6">
                  {cookieCategories.map(({ key, icon: Icon, title, description, examples, duration }) => (
                    <div key={key} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg theme-black:bg-white/40 theme-black:dark:bg-gray-800/40 theme-black:backdrop-blur-md">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <Icon className="h-5 w-5 text-blue-600 mt-1" />
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white">{title}</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{description}</p>
                            <div className="space-y-1 text-xs text-gray-500 dark:text-gray-500">
                              <p>{examples}</p>
                              <p>{duration}</p>
                            </div>
                          </div>
                        </div>
                        
                        <label className="relative inline-flex items-center cursor-pointer ml-4">
                          <input
                            type="checkbox"
                            checked={consent[key]}
                            onChange={() => toggleCategory(key)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 dark:peer-focus:ring-yellow-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-yellow-500"></div>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Legal information */}
                <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    {t('cookies.preferences.legalInfo', 'Legal information')}
                  </h4>
                  <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                    <p>
                      {t('cookies.preferences.gdprCompliance', 'Under GDPR, your consents are kept for a maximum of 12 months.')}
                    </p>
                    <p>
                      {t('cookies.preferences.dataController', 'Responsable de traitement : Komby SAS')}
                    </p>
                    <p>
                      {t('cookies.preferences.rights', 'Vous disposez d\'un droit d\'accès, de rectification et de suppression de vos données.')}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Actions */}
          <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 p-6">
            <div className="flex flex-col sm:flex-row gap-3 justify-between">
              <button
                onClick={revokeAllConsents}
                disabled={isSaving || isLoading}
                className="px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                {t('cookies.preferences.revokeAll', 'Revoke all')}
              </button>
              
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  disabled={isSaving}
                  className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                >
                  {t('cookies.preferences.cancel', 'Cancel')}
                </button>
                
                <button
                  onClick={savePreferences}
                  disabled={isSaving || isLoading}
                  className="px-6 py-3 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {isSaving ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {isSaving 
                    ? t('cookies.preferences.saving', 'Sauvegarde...') 
                    : t('cookies.preferences.save', 'Sauvegarder')
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePreferences; 